import {last, throttle} from "lodash"
import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import {useNavigate} from "react-router-dom"

import {useTranslation} from "@frontend/i18n"
import {makePath} from "@frontend/routing"
import {trpc} from "@frontend/utils/trpc"
import {type TrackEventName, trackEvent} from "@frontend/utils/trackEvent"
import {useBooleanState} from "@frontend/utils/useBooleanState"
import {useEffectOnMount} from "@frontend/utils/useEffectOnMount"
import {CONTACT_A_TECHNICIAN_OPTION} from "@ri2/app/ai/client"
import {
  ASK_FOR_MODEL_NUMBER,
  type CaseEvent,
  MessageImage,
  MessageMedia,
  confirmationThreshold,
  findLastMap,
  getMediaFromMessage,
  isCauseOnMessageConfirmed,
  isMessageVideo,
} from "@ri2/db/client"
import {css, cx} from "@styled-system/css"
import {hstack} from "@styled-system/patterns"

import {AIDisclaimer} from "./AIDisclaimer"
import {ConversationActions} from "./ConversationActions"
import {Message} from "./Message"
import {SuggestedPartCard} from "./SuggestedPartCard"
import {ConversationInputField} from "./conversation-input-field"
import {CopyLink} from "./copy-link"
import {messageContainerClass} from "./event-layout"
import {MediaGallery} from "./media-gallery"
import {ResolveEvent} from "./resolve-event"
import {
  OPTIMISTIC_AI_MESSAGE_ID,
  OPTIMISTIC_HUMAN_MESSAGE_ID,
} from "../util/useSendMessage"

interface Props {
  caseId: string
  resolved: boolean
  events: CaseEvent[]
  sendMessage: (message: string, images?: MessageImage[]) => void
  abortSendMessage: () => void
  isReceivingMessage: boolean
  receivingMessageStatus: string | null
  title: string
  className?: string
  compact?: boolean
  /** Model number of the case product, used for part cards in compact/widget mode. */
  modelNumber?: string | null
}

export const Conversation: FC<Props> = ({
  caseId,
  events,
  sendMessage,
  abortSendMessage,
  isReceivingMessage,
  receivingMessageStatus,
  className,
  title,
  compact = false,
  modelNumber,
}) => {
  const t = useTranslation()
  const utils = trpc.useUtils()
  const [selectedMediaUrl, setSelectedImage] = useState<string | null>(null)
  const [scrollUpSize, setScrollUpSize] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const trackedAiMessageIdsRef = useRef(
    new Set(getTrackableAiMessageIds(events)),
  )
  const enteredWorkflowRef = useRef(new Set(getExistingWorkflows(events)))
  const trackedDiagnosisProbabilityKeysRef = useRef(
    new Set(getExistingDiagnosisProbabilityTrackingKeys(events)),
  )
  const hasTrackedEngagementRef = useRef(false)
  const hasTrackedResumedFromExternalRef = useRef(false)

  // Full chat route only: fire once per mount when the user arrives from an
  // external source (empty or cross-origin referrer). Every visit to this
  // route technically carries a caseId in the URL, so referrer is the only
  // practical signal that the link was opened from outside the app — the
  // closest proxy for a Continue Later / shared-link return.
  useEffect(() => {
    if (compact) return
    if (hasTrackedResumedFromExternalRef.current) return
    if (typeof window === "undefined") return

    const referrer = document.referrer
    const isExternal =
      !referrer ||
      (() => {
        try {
          return new URL(referrer).origin !== window.location.origin
        } catch {
          return true
        }
      })()

    if (!isExternal) return

    hasTrackedResumedFromExternalRef.current = true
    trackEvent("chat_resumed_from_external", caseId)
  }, [caseId, compact])

  const {
    setFalse: dismissMediaGallery,
    setTrue: openMediaGallery,
    state: showMediaGallery,
  } = useBooleanState(false)
  const [message, setMessage] = useState("")
  const [images, setImages] = useState<MessageImage[]>([])

  const onSendMessage = (
    message: string,
    source: "typed" | "quick_response",
  ): void => {
    setMessage("")
    setImages([])
    sendMessage(message, images)

    if (compact) {
      trackEvent("widget_chat_message_sent", caseId)
      trackEvent(
        source === "quick_response"
          ? "widget_chat_quick_response_sent"
          : "widget_chat_typed_message_sent",
        caseId,
      )
    } else {
      trackEvent("chat_message_sent", caseId)
      trackEvent(
        source === "quick_response"
          ? "chat_quick_response_sent"
          : "chat_typed_message_sent",
        caseId,
      )
    }
  }

  const onSend = (): void => {
    onSendMessage(message, "typed")
  }

  const navigate = useNavigate()

  const onChooseMultipleChoiceOption = (option: string): void => {
    const lastEvent = last(events)

    // If the user chose a "Contact a technician" option in response, show the peek
    if (
      lastEvent &&
      lastEvent.eventType === "message" &&
      (lastEvent.mode === "ASK_WHETHER_TO_PROCEED_WITH_DIFFICULT_QUESTIONS" ||
        lastEvent.mode === "ASK_WHETHER_TO_PROCEED_DIAGNOSING_UNUSUAL_CAUSE") &&
      option === CONTACT_A_TECHNICIAN_OPTION
    ) {
      navigate(
        makePath({
          name: "cases",
          caseId,
          causeOnMessageId: "peek",
          recommendation: "difmRemediation",
        }),
      )

      return
    }

    onSendMessage(option, "quick_response")
  }

  const inputRef = useRef<HTMLTextAreaElement>(null)

  const blurInput = useCallback(() => {
    inputRef.current?.blur()
  }, [])

  const {
    state: hasNewMessage,
    setFalse: clearNewMessage,
    setTrue: setNewMessage,
  } = useBooleanState(false)
  const {
    state: isAtTheBottom,
    setFalse: setIsNotAtTheBottom,
    setTrue: setIsAtTheBottom,
  } = useBooleanState(true)
  const {
    state: shouldAutoScroll,
    setFalse: disableAutoScroll,
    setTrue: enableAutoScroll,
  } = useBooleanState(true)
  const scrollRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const hasDoneInitialScroll = useRef(false)
  const scrollPositionRef = useRef({scrollTop: 0, atBottom: true})

  const syncScrollState = useCallback((): void => {
    const el = scrollRef.current
    if (!el) return

    const {scrollHeight, clientHeight, scrollTop} = el
    const pixelThresholdForBeingAtBottom = 100
    const isScrollAtTheBottom =
      Math.abs(scrollTop + clientHeight - scrollHeight) <
      pixelThresholdForBeingAtBottom

    scrollPositionRef.current = {scrollTop, atBottom: isScrollAtTheBottom}

    setScrollUpSize(scrollHeight - (scrollTop + clientHeight))

    if (isScrollAtTheBottom) {
      setIsAtTheBottom()
      enableAutoScroll()
      clearNewMessage()
    } else {
      setIsNotAtTheBottom()
      disableAutoScroll()
    }
  }, [
    clearNewMessage,
    disableAutoScroll,
    enableAutoScroll,
    setIsAtTheBottom,
    setIsNotAtTheBottom,
  ])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scrollToBottom = useCallback(
    throttle((scrollToTopOfLatestMessage = true): void => {
      // When we are scrolling in response to new messages, we want to scroll so
      // the top of last message is as close as possible to the top of the vieweable
      // chat area. When we are scrolling in response to the down arrow button or
      // the initial navigation to the page, we want to scroll to the very
      // bottom of the chat container.
      if (!scrollRef.current) return

      const latestMessageElement = last(
        scrollRef.current.getElementsByClassName(messageContainerClass) || [],
      )

      if (latestMessageElement && scrollToTopOfLatestMessage) {
        latestMessageElement.scrollIntoView({
          block: "start",
          inline: "nearest",
          behavior: "smooth",
        })
      } else {
        scrollRef.current.scrollTo({
          top: scrollRef.current.scrollHeight - scrollRef.current.clientHeight,
        })
      }
    }, 500),
    [],
  )

  // Track scroll position and sync UI state. Uses a ref so the
  // ResizeObserver callback always reads the latest position.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    el.addEventListener("scroll", syncScrollState, {passive: true})
    syncScrollState()
    return (): void => el.removeEventListener("scroll", syncScrollState)
  }, [syncScrollState])

  // Preserve scroll position when the container resizes (e.g. the host page
  // updates slideout panel dimensions during a client-side navigation) or the
  // message list grows while the reader is stationary.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const contentEl = contentRef.current

    const ro = new ResizeObserver(() => {
      const {atBottom, scrollTop} = scrollPositionRef.current
      if (atBottom) {
        el.scrollTop = el.scrollHeight - el.clientHeight
      } else {
        el.scrollTop = scrollTop
      }

      syncScrollState()
    })

    ro.observe(el)
    if (contentEl) {
      ro.observe(contentEl)
    }
    return (): void => ro.disconnect()
  }, [syncScrollState])

  const lastEvent = last(events)
  const lastTrackableAiMessage = [...events]
    .reverse()
    .find(isTrackableAiMessage)
  const lastEventContent =
    lastEvent?.eventType === "message" ? lastEvent.content : ""

  useLayoutEffect(() => {
    syncScrollState()
  }, [lastEventContent, syncScrollState])

  const isAskingForModelNumber =
    lastEvent?.eventType === "message" &&
    lastEvent.type === "ai" &&
    lastEvent.mode === ASK_FOR_MODEL_NUMBER

  // After the user submits a message, we optimistically insert an empty AI
  // message. Follow that new content only when the reader was already at the
  // bottom; if they've scrolled up to review prior messages, preserve their
  // position instead of snapping them back down.
  useEffect(() => {
    if (!hasDoneInitialScroll.current) return

    const didHumanJustSubmitMsg =
      lastEvent &&
      lastEvent.eventType === "message" &&
      lastEvent.type === "ai" &&
      lastEvent.content.question === ""

    if (shouldAutoScroll) {
      scrollToBottom()
    }

    if (!isAtTheBottom && !didHumanJustSubmitMsg) {
      setNewMessage()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastEventContent])

  useEffect(() => {
    scrollToBottom(false)
    hasDoneInitialScroll.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffectOnMount(() => {
    inputRef.current?.focus()
  })

  const registeredVisibleHumanMessageCount =
    getRegisteredVisibleHumanMessageCount(events)
  const engagementEventName: TrackEventName = compact
    ? "widget_chat_engaged"
    : "chat_engaged"
  const {
    data: hasTrackedEngagement,
    isSuccess: hasLoadedTrackedEngagement,
  } = trpc.hasCaseTrackingEvent.useQuery({
    caseId,
    eventName: engagementEventName,
  })

  useEffect(() => {
    if (hasTrackedEngagement) {
      hasTrackedEngagementRef.current = true
    }
  }, [hasTrackedEngagement])

  useEffect(() => {
    if (registeredVisibleHumanMessageCount < 2) return
    if (!hasLoadedTrackedEngagement) return
    if (hasTrackedEngagementRef.current) return

    trackEvent(engagementEventName, caseId)
    hasTrackedEngagementRef.current = true

    utils.hasCaseTrackingEvent.setData(
      {
        caseId,
        eventName: engagementEventName,
      },
      true,
    )
  }, [
    caseId,
    engagementEventName,
    hasLoadedTrackedEngagement,
    registeredVisibleHumanMessageCount,
    utils.hasCaseTrackingEvent,
  ])

  useEffect(() => {
    if (!lastTrackableAiMessage) return
    if (trackedAiMessageIdsRef.current.has(lastTrackableAiMessage.id)) return

    trackedAiMessageIdsRef.current.add(lastTrackableAiMessage.id)

    const workflow = getWorkflowForAiMessage(lastTrackableAiMessage)

    if (workflow && !enteredWorkflowRef.current.has(workflow)) {
      trackEvent(getWorkflowEntryEventName(workflow, compact), caseId)
      enteredWorkflowRef.current.add(workflow)
    }

    if (
      workflow === "ecommerce" &&
      shouldTrackPartsOutcome(lastTrackableAiMessage)
    ) {
      trackEvent(
        getSurfacedPartCount(lastTrackableAiMessage) > 0
          ? compact
            ? "widget_chat_parts_found"
            : "chat_parts_found"
          : compact
            ? "widget_chat_parts_not_found"
            : "chat_parts_not_found",
        caseId,
      )
    }

    if (workflow === "diagnosis") {
      for (const cause of lastTrackableAiMessage.causes) {
        const diagnosisProbabilityEvents: Array<{
          eventName: TrackEventName
          threshold: number
          trackingKey: string
        }> = [
          {
            eventName: "diagnosis_probability_exceeds_60",
            threshold: DIAGNOSIS_PROBABILITY_60_THRESHOLD,
            trackingKey: `${cause.causeId}:60`,
          },
          {
            eventName: "diagnosis_probability_exceeds_90",
            threshold: confirmationThreshold,
            trackingKey: `${cause.causeId}:90`,
          },
        ]

        for (const {
          eventName,
          threshold,
          trackingKey,
        } of diagnosisProbabilityEvents) {
          if (cause.probability < threshold) continue
          if (trackedDiagnosisProbabilityKeysRef.current.has(trackingKey)) {
            continue
          }

          trackEvent(eventName, caseId)
          trackedDiagnosisProbabilityKeysRef.current.add(trackingKey)
        }
      }
    }
  }, [caseId, compact, lastTrackableAiMessage])

  const onTouchMove = (): void => {
    blurInput()
    disableAutoScroll()
  }

  const multimedia = events
    .flatMap((event) =>
      event.eventType === "message" ? getMediaFromMessage(event) : [],
    )
    .reduce((prev, curr) => {
      if (prev.includes(curr)) {
        return prev
      }
      return [...prev, curr]
    }, [] as MessageMedia[])

  const onOpenMediaGallery = (image: string): void => {
    openMediaGallery()
    setSelectedImage(image)
  }

  const filteredMultimedia = multimedia.filter(({url}) => !!url)

  const initialImageIndex = selectedMediaUrl
    ? filteredMultimedia
        .map((media) =>
          isMessageVideo(media) ? media.thumbnailUrl : media.url,
        )
        .indexOf(selectedMediaUrl)
    : 0

  return (
    <>
      {filteredMultimedia.length > 0 && (
        <MediaGallery
          show={showMediaGallery}
          title={title}
          onDismiss={dismissMediaGallery}
          media={filteredMultimedia}
          initialImageIndex={initialImageIndex}
        />
      )}
      <div
        role="main"
        aria-label={t("conversation.ariaLabel")}
        onTouchMove={blurInput}
        className={cx(
          css({
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            desktopDown: {
              backgroundColor: "white",
            },
          }),
          className,
        )}
      >
        <div
          onTouchMove={onTouchMove}
          className={cx(
            css({
              flex: 1,
              paddingX: compact ? 12 : 32,
              overflowY: "auto",
            }),
          )}
          ref={scrollRef}
        >
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              gap: compact ? 10 : 16,
              marginTop: compact ? 10 : 16,
              desktop: {
                paddingX: compact ? 0 : 8,
              },
            })}
            ref={contentRef}
          >
            <CopyLink caseId={caseId} />
            {events.some(isVisibleHumanMessage) && <AIDisclaimer />}
            <>
              {events.map((event, index) => {
                if (event.eventType === "message") {
                  if (event.type === "human" && event.content.isHidden) {
                    return null
                  }
                  const isMostRecent = index === events.length - 1

                  const previouslyConfirmedCauseIds =
                    findLastMap(events.slice(0, index), (event) => {
                      if (
                        event.eventType !== "message" ||
                        event.type !== "ai"
                      ) {
                        return undefined
                      }
                      const confirmed = event.causes.filter(
                        isCauseOnMessageConfirmed,
                      )
                      return confirmed.map(({causeId}) => causeId)
                    }) ?? []

                  const suggestedParts =
                    event.type === "ai"
                      ? (event.content.suggestedParts ?? [])
                      : []

                  return (
                    <div
                      key={`message-${event.id}`}
                      className={css({
                        display: "flex",
                        flexDirection: "column",
                      })}
                    >
                      <Message
                        caseId={caseId}
                        message={event}
                        previouslyConfirmedCauseIds={
                          previouslyConfirmedCauseIds
                        }
                        isMostRecentMessage={isMostRecent}
                        isReceivingMessage={
                          event.id === OPTIMISTIC_AI_MESSAGE_ID &&
                          isReceivingMessage
                        }
                        receivingMessageStatus={receivingMessageStatus}
                        onSelectMedia={onOpenMediaGallery}
                        compact={compact}
                        modelNumber={modelNumber}
                      />
                      {suggestedParts.length > 0 && (
                        <div
                          className={css({
                            paddingLeft: "32px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            marginTop: "-8px",
                          })}
                        >
                          {suggestedParts.map((sp) => (
                            <SuggestedPartCard
                              key={sp.part.id}
                              suggestedPart={sp}
                              caseId={caseId}
                              modelNumber={modelNumber ?? null}
                              compact={compact}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )
                } else if (event.eventType === "resolveEvent") {
                  return (
                    <ResolveEvent
                      resolveEvent={event}
                      key={`resolveEvent-${event.id}`}
                    />
                  )
                } else {
                  return null
                }
              })}
            </>

            <ConversationActions
              scrollUpSize={scrollUpSize}
              scrollToBottom={scrollToBottom}
              hasNewMessage={hasNewMessage}
              events={events}
              onChooseMultipleChoiceOption={onChooseMultipleChoiceOption}
              compact={compact}
              onUploadPhoto={() => fileInputRef.current?.click()}
              caseId={caseId}
            />
          </div>
        </div>
        <div
          className={cx(
            css({
              flex: 0,
              height: "auto",
              zIndex: 10,
              paddingLeft: compact ? 10 : 19,
              paddingRight: compact ? 10 : 21,
              paddingBottom: compact ? 8 : 16,
              desktop: {
                position: "relative",
                paddingX: compact ? 12 : 32,
              },
            }),
            hstack({gap: 8, alignItems: "flex-end"}),
          )}
        >
          <ConversationInputField
            value={message}
            images={images}
            onFocus={() => {
              scrollToBottom(false)
            }}
            onChange={setMessage}
            onImagesChange={setImages}
            onSend={onSend}
            onAbort={abortSendMessage}
            disabled={isReceivingMessage}
            isReceiving={isReceivingMessage}
            inputRef={inputRef}
            allowImageUpload={isAskingForModelNumber}
            fileInputRef={fileInputRef}
            animatedGradientBorder={compact}
            className={css({
              width: "100%",
              backgroundColor: "white",
            })}
          />
        </div>
      </div>
    </>
  )
}

type Workflow = "diagnosis" | "ecommerce" | "customer_service" | "unsupported"

type AiMessageEvent = Extract<CaseEvent, {eventType: "message"}> & {type: "ai"}

const DIAGNOSIS_PROBABILITY_60_THRESHOLD = 0.6

const isTrackableAiMessage = (event: CaseEvent): event is AiMessageEvent =>
  event.eventType === "message" &&
  event.type === "ai" &&
  event.id !== OPTIMISTIC_AI_MESSAGE_ID

const isVisibleHumanMessage = (
  event: CaseEvent,
): event is Extract<CaseEvent, {eventType: "message"; type: "human"}> =>
  event.eventType === "message" &&
  event.type === "human" &&
  !event.content.isHidden

const isRegisteredVisibleHumanMessage = (
  event: CaseEvent,
): event is Extract<CaseEvent, {eventType: "message"; type: "human"}> =>
  isVisibleHumanMessage(event) && event.id !== OPTIMISTIC_HUMAN_MESSAGE_ID

const getRegisteredVisibleHumanMessageCount = (events: CaseEvent[]): number =>
  events.filter(isRegisteredVisibleHumanMessage).length

const getTrackableAiMessageIds = (events: CaseEvent[]): string[] =>
  events.filter(isTrackableAiMessage).map((event) => event.id)

const getExistingWorkflows = (events: CaseEvent[]): Workflow[] => {
  const workflows: Workflow[] = []

  for (const event of events) {
    if (!isTrackableAiMessage(event)) continue

    const workflow = getWorkflowForAiMessage(event)
    if (workflow) {
      workflows.push(workflow)
    }
  }

  return workflows
}

const getDiagnosisTrackingKeysForMessage = (event: AiMessageEvent): string[] =>
  event.causes.flatMap((cause) => {
    const trackingKeys: string[] = []

    if (cause.probability >= DIAGNOSIS_PROBABILITY_60_THRESHOLD) {
      trackingKeys.push(`${cause.causeId}:60`)
    }

    if (cause.probability >= confirmationThreshold) {
      trackingKeys.push(`${cause.causeId}:90`)
    }

    return trackingKeys
  })

const getExistingDiagnosisProbabilityTrackingKeys = (
  events: CaseEvent[],
): string[] =>
  events
    .filter(isTrackableAiMessage)
    .flatMap(getDiagnosisTrackingKeysForMessage)

const getWorkflowEntryEventName = (
  workflow: Workflow,
  compact: boolean,
): TrackEventName => {
  const prefix = compact ? "widget_chat" : "chat"

  switch (workflow) {
    case "diagnosis":
      return `${prefix}_workflow_diagnosis_entered`
    case "ecommerce":
      return `${prefix}_workflow_ecommerce_entered`
    case "customer_service":
      return `${prefix}_workflow_customer_service_entered`
    case "unsupported":
      return `${prefix}_workflow_unsupported_entered`
  }
}

const getWorkflowForAiMessage = (event: AiMessageEvent): Workflow | null => {
  const workflowFromDetector = getWorkflowFromDetector(event)
  if (workflowFromDetector) {
    return workflowFromDetector
  }

  if (isUnsupportedMode(event.mode)) {
    return "unsupported"
  }

  if (isDiagnosisMode(event.mode)) {
    return "diagnosis"
  }

  if (event.mode === "CONTENT_DELIVERED") {
    return "ecommerce"
  }

  return null
}

const getWorkflowFromDetector = (event: AiMessageEvent): Workflow | null => {
  switch (event.content.workflowCategory) {
    case "DIAGNOSIS":
      return "diagnosis"
    case "ECOMMERCE":
      return "ecommerce"
    case "CUSTOMER_SERVICE":
      return "customer_service"
    case "UNSUPPORTED":
      return "unsupported"
    default:
      return null
  }
}

const isUnsupportedMode = (mode: string | null): boolean =>
  mode === "UNSUPPORTED_WORKFLOW" || mode === "UNSUPPORTED_APPLIANCE_TYPE"

const isDiagnosisMode = (mode: string | null): boolean =>
  !!mode &&
  [
    "ASK_FOR_SYMPTOM_DETAILS",
    "ASK_FOR_SKILL_LEVEL",
    "ASK_FOR_APPLIANCE_AGE",
    "ASK_FOR_ADDITIONAL_SYMPTOMS_AND_HISTORY",
    "ASK_DIAGNOSTIC_QUESTION",
    "ASK_WHETHER_TO_PROCEED_WITH_DIFFICULT_QUESTIONS",
    "ASK_WHETHER_TO_PROCEED_DIAGNOSING_UNUSUAL_CAUSE",
    "MAKE_DIAGNOSIS",
  ].includes(mode)

const getSurfacedPartCount = (event: AiMessageEvent): number => {
  const partIds = new Set<number>()

  if (event.content.confirmedPart) {
    partIds.add(event.content.confirmedPart.id)
  }

  for (const part of event.content.confirmedParts ?? []) {
    partIds.add(part.id)
  }

  for (const suggestedPart of event.content.suggestedParts ?? []) {
    partIds.add(suggestedPart.part.id)
  }

  return partIds.size
}

const shouldTrackPartsOutcome = (event: AiMessageEvent): boolean => {
  if (getSurfacedPartCount(event) > 0) {
    return true
  }

  return /no parts? found|no matching parts?|couldn'?t find (any )?parts?/.test(
    event.content.question.toLowerCase(),
  )
}
